/**
 * ColorPickerPopover — reusable spectrum/HSV color picker.
 *
 * A clickable color SWATCH opens a popover containing a graphics-editor
 * style picker: a saturation/brightness square plus a hue strip (from
 * `react-colorful`, the ~2.8KB standard), and a two-way-synced hex text
 * input. The full color space is reachable — any hue, saturation and
 * brightness — not just a fixed preset grid.
 *
 * Optional `presets` render as a small swatch row inside the popover for
 * one-click convenience; they do NOT replace the spectrum.
 *
 * ADR-057: the popover chrome stays monochrome (border / background
 * tokens). Only the swatch and the picker's own gradient carry color.
 */
import { useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';

import { cn } from '../../lib/utils';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

/** Normalise loose hex input to a `#rrggbb` lowercase string, or null. */
function toHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!HEX_RE.test(v)) return null;
  return (v.startsWith('#') ? v : `#${v}`).toLowerCase();
}

export interface ColorPickerPopoverProps {
  /** Current color (any CSS-acceptable string; spectrum needs hex). */
  value: string | null | undefined;
  /** Fired with a `#rrggbb` hex string whenever the color changes. */
  onChange: (hex: string) => void;
  /** Optional one-click preset swatches shown inside the popover. */
  presets?: ReadonlyArray<{ value: string; label?: string }>;
  /** Optional handler for preset clicks (lets a constrained consumer
   *  emit a named key instead of the preset's raw hex). Falls back to
   *  `onChange(preset.value)`. */
  onPresetSelect?: (presetValue: string) => void;
  disabled?: boolean;
  /** Aria label for the swatch trigger. */
  ariaLabel?: string;
  /** Fallback swatch fill when `value` isn't a renderable color. */
  fallbackSwatch?: string;
  /** Extra classes on the swatch trigger. */
  triggerClassName?: string;
  /** Render the hex text input alongside the spectrum (default true). */
  showHexInput?: boolean;
  'data-testid'?: string;
}

export function ColorPickerPopover({
  value,
  onChange,
  presets,
  onPresetSelect,
  disabled,
  ariaLabel = 'Pick color',
  fallbackSwatch,
  triggerClassName,
  showHexInput = true,
  'data-testid': testId,
}: ColorPickerPopoverProps) {
  const hex = toHex(value);
  // Local text state so the user can type partial hex without the
  // parent rejecting it mid-keystroke. Re-syncs when `value` changes.
  const [text, setText] = useState(hex ?? (value ?? ''));
  useEffect(() => {
    setText(hex ?? (value ?? ''));
  }, [hex, value]);

  const swatchFill = hex ?? value ?? fallbackSwatch ?? 'transparent';
  // react-colorful needs a concrete hex; default to a neutral when the
  // current value isn't a hex (e.g. a named token on the status path).
  const pickerColor = hex ?? '#64748b';

  const commit = (next: string) => {
    const normalised = toHex(next);
    if (normalised) onChange(normalised);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          title={ariaLabel}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName,
          )}
          style={{ backgroundColor: swatchFill }}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[232px] space-y-3 p-3">
        <HexColorPicker
          color={pickerColor}
          onChange={(c) => {
            setText(c);
            commit(c);
          }}
          aria-label="Color spectrum"
        />
        {showHexInput && (
          <Input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              commit(e.target.value);
            }}
            spellCheck={false}
            className="h-8 font-mono text-xs"
            placeholder="#64748b"
            aria-label="Hex color value"
          />
        )}
        {presets && presets.length > 0 && (
          <div className="grid grid-cols-5 gap-1.5 pt-1">
            {presets.map((p) => {
              const fill = toHex(p.value) ?? p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  aria-label={p.label ?? p.value}
                  title={p.label ?? p.value}
                  onClick={() =>
                    onPresetSelect ? onPresetSelect(p.value) : onChange(p.value)
                  }
                  className="h-6 w-6 rounded-full border border-border/60 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: fill === 'transparent' ? undefined : fill,
                  }}
                />
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
